import type { FC } from 'react';
import { useRef, useState } from 'react';
import useSWRImmutable from 'swr/immutable';
import { useRouter } from 'next/router';

import { Box, useIntersectionObserver } from '@packages/shared';
import { ProductCard } from '@packages/modules';
import { useIsBot } from '@packages/tracking';
import type { ProductCardSpecificProps } from '@packages/modules/src/ProductCard/types';
import { rememberPageData } from '@packages/shared/src/hooks/useScrollRestoration';
import { useConfig } from '@packages/utilities';

import { SEARCH_PRODUCT_URL } from '../../api/rest/endpoints';
import type {
  CurrentNavigation,
  ProductVariation,
  SearchProductApiRequestOptions,
} from '../../api/rest/types';
import { searchProductFetcher } from '../../api/rest/fetchers';
import type { DesktopDisplayMode } from '../../types';

import { getHeightHeuristic } from './heightHeuristic';
import { useProductImpressionTracking } from './useProductImpressionTracking';
import { useProductClickTracking } from './useProductClickTracking';
import { useVariationDataForProductCard } from './useVariationDataForProductCard';

export type ActualSearchProductCardProps = {
  product: Pick<
    ProductCardSpecificProps,
    | 'sku'
    | 'brand'
    | 'title'
    | 'flags'
    | 'additionalFlags'
    | 'datasheet'
    | 'highlight'
    | 'additionalInfo'
  > & {
    variations: ProductVariation[];
    masterSku: string;
    productPosition?: number;
    avgRating?: number;
    mkz: {
      id: number;
      category: string;
      superId?: string;
    };
  };
  requestCacheKey: string;
  categoryDataForTracking: {
    breadcrumbData?: CurrentNavigation[];
    currentCategoryName?: string;
  };
  displayMode?: DesktopDisplayMode;
} & Pick<
  ProductCardSpecificProps,
  'priority' | 'useAdditionalFlagsSpacer' | 'useRatingsSpacer' | 'isSponsored'
> &
  Pick<ProductCardSpecificProps['price'], 'useUnitPriceSpacer'>;

/**
 * A search-page-specific wrapper around `ProductCard`
 */
const ActualSearchProductCard: FC<ActualSearchProductCardProps> = (props) => {
  // local state to allow changing colors, also used for fetching full product data
  const [activeVariationIndex, setActiveVariationIndex] = useState(0);

  const {
    product,
    requestCacheKey,
    categoryDataForTracking,
    useUnitPriceSpacer,
    displayMode,
    ...passthroughProps
  } = props;
  const { sku, masterSku, avgRating } = product;

  const { clientId } = useConfig();

  const options: SearchProductApiRequestOptions = {
    sku: masterSku,
    requestCacheKey,
    clientId,
  };

  const { data, error } = useSWRImmutable(
    // conditional fetching of full variation data happens only when the user switches away from the default color
    activeVariationIndex ? [SEARCH_PRODUCT_URL, options] : null,
    searchProductFetcher,
    {
      suspense: false,
    },
  );

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  const activeVariation = data?.variations?.[activeVariationIndex] ?? product.variations[0];

  const { dispatchProductClickTracking, productClickTrackingProps } = useProductClickTracking(
    product,
    activeVariation,
    categoryDataForTracking,
  );
  const activeVariationProps = useVariationDataForProductCard(
    product,
    activeVariationIndex,
    activeVariation,
    useUnitPriceSpacer,
    displayMode,
  );

  const { asPath } = useRouter();

  const handleClick = () => {
    dispatchProductClickTracking();
    rememberPageData(sku, asPath);
  };

  if (error) {
    // Handle error state
    return <div>Error loading data</div>;
  }

  return (
    <ProductCard
      {...passthroughProps}
      {...activeVariationProps}
      clickTrackingProps={productClickTrackingProps}
      shrinkToFit
      onColorChange={setActiveVariationIndex}
      onProductLinkActionAreaClick={handleClick}
      rating={avgRating ? { avgRating, reviewCount: 0 } : undefined}
    />
  );
};

export type SearchProductCardProps = ActualSearchProductCardProps & {
  alwaysRender?: boolean;
  displayMode: DesktopDisplayMode;
  onFirstBecameVisible?: () => void;
};

/**
 * Specialized version of `ProductCard` for the search product grids
 */
export const SearchProductCard: FC<SearchProductCardProps> = (props) => {
  const { alwaysRender, displayMode, onFirstBecameVisible, ...actualSearchProductCardProps } =
    props;
  const {
    product: { masterSku, title },
    product,
    categoryDataForTracking: categoryData,
  } = props;

  const wrapper = useRef<HTMLDivElement>(null);

  const { dispatchProductImpressionTracking } = useProductImpressionTracking(product, categoryData);

  // check if the card is fully visible
  useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting) return;
      // intersectOnce is set to true, so this callback will only be called once
      onFirstBecameVisible?.();
      dispatchProductImpressionTracking();
    },
  });

  // check if the card will scroll into view soon (as defined by the margin)
  const { isIntersecting: shouldRender } = useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 0,
    rootMargin: '500px 0px 500px 0px',
  });

  const isBot = useIsBot();

  const showActualCard = alwaysRender || shouldRender || isBot;

  return (
    <Box
      ref={wrapper}
      sx={{ height: showActualCard ? '100%' : getHeightHeuristic(props, displayMode) }}
      id={masterSku}
    >
      {showActualCard ? (
        <ActualSearchProductCard {...actualSearchProductCardProps} displayMode={displayMode} />
      ) : (
        title
      )}
    </Box>
  );
};
